import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import React, { useEffect, useState } from 'react';

import { Icon } from '@/components/core/Icon';
import { useTranslation } from '@/hooks/useTranslation';

import { usePreviewValidator } from '../datamodel/hooks/usePreviewValidator';
import { NonDrawableURLPreview } from '../datamodel/Image/NonDrawableURLPreview';

interface FilePreviewProps {
  url: string;
  alt?: string;
  className?: string;
  showAsIconPreview?: boolean;
}

const FilePreview: React.FC<FilePreviewProps> = ({ url, alt = 'Preview', className, showAsIconPreview = false }) => {
    const { validatePreviewAsync } = usePreviewValidator();
    const { t } = useTranslation();
    const [isPreviewUrlDrawable, setIsPreviewUrlDrawable] = useState(false);
    const [mimeType, setMimeType] = useState<string | undefined>();
    const [isValidating, setIsValidating] = useState(true);
    const [imageLoadError, setImageLoadError] = useState(false);

    const checkIfUrlDrawableAndPreviewAsync = async (urlToCheck: string) => {
        setIsValidating(true);
        setImageLoadError(false);
        try {
            const { mimeType: resolvedMimeType, drawable } = await validatePreviewAsync(urlToCheck);
            setMimeType(resolvedMimeType);
            setIsPreviewUrlDrawable(drawable);
            setIsValidating(false);
        } catch (error) {
            if (axios.isCancel(error)) {
                return;
            }
            setMimeType(undefined);
            setIsPreviewUrlDrawable(false);
            setIsValidating(false);
        }
    };

    useEffect(() => {
        async function checkInitialInput() {
            if (url) {
                await checkIfUrlDrawableAndPreviewAsync(url);
            } else {
                setIsValidating(false);
                setImageLoadError(false);
            }
        }

        checkInitialInput();
        //eslint-disable-next-line
    }, [url]);

    if (!url) {
        return null;
    }

    // Show loading state while validating
    if (isValidating) {
        return showAsIconPreview ? (
            <div className="tw-w-8 tw-h-8 tw-bg-gray-200 tw-animate-pulse tw-rounded" />
        ) : (
            <div className="tw-w-[248px] tw-h-[350px] tw-bg-gray-200 tw-animate-pulse tw-rounded" />
        );
    }

    if (showAsIconPreview) {
        if (imageLoadError) {
            return (
                <Icon
                    icon={ExclamationTriangleIcon}
                    size="sm"
                    variant="destructive"
                    colorScheme="dark"
                />
            );
        }
        if (!isPreviewUrlDrawable && mimeType !== undefined) {
            return <NonDrawableURLPreview mimeType={mimeType} />;
        }
        return (
            <img
                src={url}
                alt={alt}
                className={className}
                loading="lazy"
                onError={() => setImageLoadError(true)}
            />
        );
    }

    if (imageLoadError) {
        return (
            <div className={`${className} tw-flex tw-flex-col tw-items-center tw-justify-center tw-relative tw-h-[250px] tw-gap-2`}>
                <Icon
                    icon={ExclamationTriangleIcon}
                    size="lg"
                    variant="destructive"
                    colorScheme="dark"
                />
                <span className="tw-text-error-dark tw-text-xs">{t('FILE_NOT_FOUND')}</span>
            </div>
        );
    }

    return (
        <>
            {isPreviewUrlDrawable ? (
                <img
                    src={url}
                    alt={alt}
                    className={`${className} image-gallery-image tw-w-[100%] tw-h-auto`}
                    loading="lazy"
                    onError={() => setImageLoadError(true)}
                />
            ) : (
                <div className={`${className} tw-flex tw-items-center tw-justify-center tw-relative tw-h-[250px]`}>
                    <NonDrawableURLPreview mimeType={mimeType} className='tw-scale-[2]'/>
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tw-absolute tw-top-0 tw-left-0 tw-w-full tw-h-full tw-z-30 tw-cursor-pointer"
                        title="Open file in new tab"
                    />
                </div>
            )}
        </>
    );
};

export default FilePreview;
